<template>
  <v-form v-if="!loading" ref="form" v-model="formValid" :disabled="newSession.cancelled">
    <section>
      <v-row no-gutters>
        <v-col cols="12">
          <TextField
            v-model="newSession.name"
            :single-line="true"
            subtitle="Nom de la Session *"
            :rules="mandatoryRule"
            :autofocus="true"
          />
        </v-col>
      </v-row>
    </section>

    <section>
      <SessionTimes :select-session-date="selectSessionDate" />
    </section>

    <section>
      <SessionModifiers v-if="canShowFieldsBasedOnSessionType(newSession.type, longSessionTypes)" />
    </section>

    <CondictonalFields :mandatory-type-session="mandatoryTypeSession" />

    <!-- todo: Move this to a json-->
    <RepriseFields :places-loading="placesLoading" @update-session-content-type="updateSessionContentTypes" />

    <v-row class="pt-5" no-gutters>
      <TextArea
        v-model="newSession.observation"
        subtitle="Observation"
        icon="mdi-message-text"
      />
    </v-row>

    <!-- action section -->
    <v-divider style="padding:10px;" />
    <v-row class="p0-32">
      <v-spacer />

      <ButtonComponent
        v-if="editable && canUserPerformAction"
        :disabled="!canUserPerformAction"
        :action="routeToProducts"
        label="Produits"
        icon="mdi-page-next"
        class="mx-2"
      />

      <ButtonComponent
        v-if="editable && canUserPerformAction"
        :disabled="newSession.cancelled || !canUserPerformAction"
        :action="changeConfirmationModal"
        label="effacer"
        icon="mdi-delete"
        class="mx-2"
      />
      <ButtonComponent
        v-if="editable && canUserPerformAction"
        :disabled="newSession.cancelled || !canUserPerformAction"
        :action="copySession"
        label="Copie"
        icon="mdi-content-copy"
        class="mx-2"
      />
      <ButtonComponent
        v-if="canUserPerformAction"
        :disabled="!formValid || newSession.cancelled || !canUserPerformAction"
        :action="handleCreateEditSession"
        :label="buttonLabel"
        icon="mdi-plus"
      />
    </v-row>

    <SessionEditOverlay
      v-if="newSession.recurring && editable"
      :activator.sync="editSessionOverlay"
      :change-confirmation-modal="changeEditSessionOverlay"
      :change-recurring-session="createEditSession"
      :options="editOverlayOptions"
    />

    <ConfirmationModal title="Supprimer la session" :activator.sync="confirmationModal" :close-modal="changeConfirmationModal" :action="deleteValidation">
      <template #body>
        <h3 class="black--text">
          Êtes-vous sûr de vouloir supprimer la session?
          Une fois supprimée, cette action ne peut pas être annulée et toutes les ressources associées seront également supprimées des sessions
        </h3>
        <RadioGroup
          v-if="canShowRadioGroup"
          v-model="deleteOption"
          :options="deleteOptions"
        />
      </template>
    </ConfirmationModal>

    <ConfirmationModal title="Supprimer la session" :activator.sync="deleteConfirmationModal" :close-modal="changeDeleteConfirmationModal" :action="deleteSession">
      <template #body>
        <h3 class="black--text">
          La (les) session(s) compte(nt) au moins un participant. <strong class="third--text">Es-tu sûr de vouloir le supprimer?</strong>
        </h3>
      </template>
    </ConfirmationModal>
    <SessionCopyModal v-if="copyDialog" @closeDialog="closeDialog" />
    <HorseOverlapConfirmationModal />
  </v-form>
  <div v-else class="full-height ">
    <div class="d-flex justify-center align-center min-height350">
      <Loader />
    </div>
  </div>
</template>

<script>

import { ref, watch, computed, provide, onBeforeMount } from '@vue/composition-api';
import { endOfDay, format, getUnixTime, add, fromUnixTime } from 'date-fns';

// helpers
import { isObjectEmpty, sendNotification, canShowFieldsBasedOnSessionType, mandatoryRule, deleteOptions, repriseSessionType, stageRadioOptions } from '@/helpers/utils';
import {
  computeSessionUuid,
  longSessionTypes,
  handleDeleteSession,
  prepareSessionBody,
  handleCreateSession,
  handleEditSession,
  constructSessionsBag,
  sessionFieldsToUpdate,
  sessionsWithPeriods
} from '@/helpers/sessions';

import Loader from '@/components/common/Loader.vue';
import TextField from '@/components/common/Form/TextField.vue';
import ButtonComponent from '@/components/common/Button.vue';
import SelectPicker from '@/components/common/Form/SelectPicker.vue';
import TextArea from '@/components/common/Form/TextArea.vue';
import RadioGroup from '@/components/common/RadioGroup.vue';

import SessionTimes from './SessionTimes.vue';
import SessionDiscipline from './SessionDiscipline.vue';
import SessionProductActivity from './SessionProductActivity.vue';
import RepriseFields from './RepriseFields.vue';
import SessionModifiers from './SessionModifiers.vue';
import CondictonalFields from './CondictonalFields.vue';
import ConfirmationModal from '@/components/common/ConfirmationModal.vue';
import SessionCopyModal from './SessionCopyModal.vue';
import SessionEditOverlay from './SessionEditOverlay.vue';
import HorseOverlapConfirmationModal from '@/components/horse/HorseOverlapConfirmationModal.vue';

import store from '@/store';
import router from '@/router';

const localReferences = require('@/configs/references.json');

export default {
  components: { RadioGroup, Loader, TextField, SessionTimes, ButtonComponent, SelectPicker, SessionDiscipline, SessionProductActivity, TextArea, RepriseFields, SessionModifiers, CondictonalFields, ConfirmationModal, SessionCopyModal, SessionEditOverlay, HorseOverlapConfirmationModal },
  props: {
    session: {
      type: Object,
      default: null
    },
    mode: {
      type: String,
      default: null
    },
    closeDialog: {
      type: Function,
      default: null
    },
    mandatoryTypeSession: {
      type: Boolean,
      default: false
    },
    selectSessionDate: {
      type: Boolean,
      default: false
    }
  },
  setup (props) {
    // variables
    const loading = ref(false);
    const placesLoading = ref(false);
    const newSession = ref({ ...props.session, periods: props.session.periods || [], product_activity: props.session.product_activity || [], form: { } });
    const productActivityDialog = ref(false);
    // content types
    const sessionContents = ref({
      place: {}
    });

    // form
    const formValid = ref(true);
    const confirmationModal = ref(false);
    const deleteConfirmationModal = ref(false);
    const copyDialog = ref(false);
    const deleteOption = ref('SINGLE');
    const editSessionOverlay = ref(false);
    const typeOfupdate = ref('SINGLE');

    const selectedDate = computed(() => store.getters.selectedDate);
    const editable = computed(() => props.mode === 'edit');
    const buttonLabel = computed(() => (editable.value ? 'Modifier' : 'Créer'));
    const sessionType = computed(() => newSession.value.type);
    const oldSessionDetail = computed(() => props.session);

    // session variables
    const sessionReplica = ref({
      isReplica: false,
      replica_type: props.session.replica && !isObjectEmpty(props.session.replica) ? props.session.replica.replica_type : null,
      replica_option: null,
      replica_interval: [],
      replica_weeks: [],
      replica_end_date: format(add(new Date(selectedDate.value), { days: 1 }), 'yyyy-MM-dd'),
      excludeHolidays: false,
      excludeVacations: false
    });

    const originalSessionReplica = ref({
      isReplica: false,
      replica_type: props.session.replica && !isObjectEmpty(props.session.replica) ? props.session.replica.replica_type : null,
      replica_option: null,
      replica_interval: [],
      replica_weeks: [],
      replica_end_date: format(add(new Date(selectedDate.value), { days: 1 }), 'yyyy-MM-dd'),
      excludeHolidays: false,
      excludeVacations: false
    });

    const canShowRadioGroup = computed(() => newSession.value.recurring || (sessionReplica.value.isReplica));

    const changeEditSessionOverlay = value => {
      editSessionOverlay.value = value;
    };

    const changeDeleteConfirmationModal = () => {
      deleteConfirmationModal.value = !deleteConfirmationModal.value;
    };

    const changeConfirmationModal = () => {
      if (!store.getters.canUserPerformAction) {
        return;
      }
      confirmationModal.value = !confirmationModal.value;
    };

    const copySession = () => {
      if (!store.getters.canUserPerformAction) {
        return;
      }
      copyDialog.value = true;
    };

    const resetParticipantAmount = () => {
      newSession.value.max_participant = props.session.max_participant;
      newSession.value.min_participant = props.session.min_participant;
    };

    const createEditSession = async () => {
      if (sessionReplica.value.isReplica && sessionReplica.value.replica_type === 'REPLICATION_SIMPLE' && !sessionReplica.value.replica_option) {
        sendNotification('error', 'L\'option de réplication est obligatoire');
        return;
      }

      if (sessionReplica.value.isReplica && sessionReplica.value.replica_type === 'REPLICATION_ADVANCE' && !sessionReplica.value.replica_interval.length) {
        sendNotification('error', 'L\'option de réplication est obligatoire');
        return;
      }

      editSessionOverlay.value = false;

      const { success, data, message, code } = prepareSessionBody(newSession.value, sessionContents.value, sessionReplica.value);

      if (!success) {
        sendNotification('error', message);

        switch (code) {
          case 'INCORRECT_PARTICIPANT_AMOUT': {
            resetParticipantAmount();
            break;
          }
          default:
        }

        return;
      }

      const session = data;
      session.uuid = editable.value ? newSession.value.uuid : computeSessionUuid(session);

      if (editable.value) {
        const fieldsToUpdate = sessionFieldsToUpdate(props.session, { ...session, end_recurring_date: getUnixTime(endOfDay(new Date(sessionReplica.value.replica_end_date))) });
        if (!fieldsToUpdate.length) {
          sendNotification('info', 'Aucun champ n\'a été modifié');
          return;
        }

        loading.value = true;

        const response = await handleEditSession(session, props.session, sessionReplica.value, typeOfupdate.value, fieldsToUpdate);

        loading.value = false;

        /* Handle horse overlap confirmation */
        if (response?.pendingOverlapConfirmation) {
          store.commit('setHorseOverlapModal', {
            show: true,
            conflicts: response.conflicts || [],
            callback: () => createEditSessionWithConfirmedOverlap()
          });
          return;
        }

        let notificationType = 'error';
        let notificationError = 'une erreur s\'est produite, veuillez réessayer';

        if (response) {
          notificationType = response.success ? 'success' : 'error';

          notificationError = response.message;

          if (response.closeDialog) {
            props.closeDialog();
          }
        }

        sendNotification(notificationType, notificationError);

        return;
      }

      loading.value = true;

      const response = await handleCreateSession(session, sessionReplica.value);

      loading.value = false;

      sendNotification(response.success ? 'success' : 'error', response.message);

      props.closeDialog();
    };

    const createEditSessionWithConfirmedOverlap = async () => {
      if (sessionReplica.value.isReplica && sessionReplica.value.replica_type === 'REPLICATION_SIMPLE' && !sessionReplica.value.replica_option) {
        sendNotification('error', 'L\'option de réplication est obligatoire');
        return;
      }

      if (sessionReplica.value.isReplica && sessionReplica.value.replica_type === 'REPLICATION_ADVANCE' && !sessionReplica.value.replica_interval.length) {
        sendNotification('error', 'L\'option de réplication est obligatoire');
        return;
      }

      const { success, data, message, code } = prepareSessionBody(newSession.value, sessionContents.value, sessionReplica.value);

      if (!success) {
        sendNotification('error', message);

        switch (code) {
          case 'INCORRECT_PARTICIPANT_AMOUT': {
            resetParticipantAmount();
            break;
          }
          default:
        }

        return;
      }

      const session = data;
      session.uuid = editable.value ? newSession.value.uuid : computeSessionUuid(session);

      loading.value = true;

      const fieldsToUpdate = sessionFieldsToUpdate(props.session, { ...session, end_recurring_date: getUnixTime(endOfDay(new Date(sessionReplica.value.replica_end_date))) });
      const response = await handleEditSession(session, props.session, sessionReplica.value, typeOfupdate.value, fieldsToUpdate, { overlapConfirmed: true });

      loading.value = false;

      let notificationType = 'error';
      let notificationError = 'une erreur s\'est produite, veuillez réessayer';

      if (response) {
        notificationType = response.success ? 'success' : 'error';

        notificationError = response.message;

        if (response.closeDialog) {
          props.closeDialog();
        }
      }

      sendNotification(notificationType, notificationError);
    };

    const handleCreateEditSession = () => {
      if (!store.getters.canUserPerformAction) {
        return null;
      }
      if (((props.session.replica && !isObjectEmpty(props.session.replica)) && !sessionReplica.value.isReplica) || !newSession.value.recurring) {
        return createEditSession();
      }

      if (editable.value && props.session.recurring) {
        changeEditSessionOverlay(true);
        return null;
      }
      return createEditSession();
    };

    const deleteSession = async () => {
      loading.value = true;
      const response = await handleDeleteSession(deleteOption.value, newSession.value);

      sendNotification(response.success ? 'success' : 'error', response.message);

      loading.value = false;
      changeConfirmationModal();
      props.closeDialog();
    };

    const deleteValidation = async () => {
      const sessions = await constructSessionsBag(deleteOption.value, newSession.value);

      const doesSessionHaveParticipant = sessions.some(s => (s.participants || []).length);

      if (!doesSessionHaveParticipant) {
        return deleteSession();
      }
      changeConfirmationModal();
      changeDeleteConfirmationModal();
    };

    const canShowGroupeEfforField = computed(() => ['GROUPE'].includes(sessionType.value));

    const updateSessionContentTypes = sessionContentTypes => {
      sessionContents.value.place = sessionContentTypes.place;
    };

    const populateSessionContents = () => {
      // reset values
      sessionContents.value.place = {};
      // place
      const placeObject = store.getters.sessionPlace(props.session);
      sessionContents.value.place = placeObject || null;
    };

    const resetReplica = () => {
      sessionReplica.value = {
        isReplica: false,
        replica_type: props.session.replica && !isObjectEmpty(props.session.replica),
        replica_option: null,
        replica_interval: [],
        replica_weeks: [],
        replica_end_date: format(add(new Date(selectedDate.value), { days: 1 }), 'yyyy-MM-dd'),
        excludeHolidays: false,
        excludeVacations: false
      };
    };

    const populateReplica = () => {
      resetReplica();
      if (props.session.replica && !isObjectEmpty(props.session.replica)) {
        sessionReplica.value = {
          ...props.session.replica,
          isReplica: true,
          replica_end_date: format(new Date(props.session.end_recurring_date * 1000), 'yyyy-MM-dd')
        };
      }
    };

    const canUserPerformAction = computed(() => store.getters.canUserPerformAction);

    watch(() => props.session, () => {
      newSession.value = { ...props.session, periods: props.session.periods || [], product_activity: props.session.product_activity || [], form: { } };
      deleteOption.value = 'SINGLE';
      typeOfupdate.value = null;
      newSession.value.periods = props.session.periods || [];
      populateReplica();
      populateSessionContents();
    });

    watch(sessionType, async () => {
      if (!longSessionTypes.includes(sessionType.value)) {
        newSession.value.recurring = false;
        newSession.value.recurring_uuid = null;
      }

      if (!repriseSessionType.includes(sessionType.value)) {
        newSession.value.replica = null;
        newSession.value.product_activity = [];
        newSession.value.discipline = null;
      }

      if (!sessionsWithPeriods.includes(sessionType.value)) {
        newSession.value.periods = [];
      }
    });

    // hooks
    onBeforeMount(async () => {
      if (!store.state.isLazyLoaded.places) {
        placesLoading.value = true;
        await store.dispatch('getPlaces');
        placesLoading.value = false;
      }
      populateReplica();
      populateSessionContents();
    });

    const editOverlayOptions = computed(() => {
      const start = format(new Date(fromUnixTime(newSession.value.date ? newSession.value.date : newSession.value.start_time)), 'yyyy-MM-dd');
      const startOld = format(new Date(fromUnixTime(oldSessionDetail.value.date ? oldSessionDetail.value.date : oldSessionDetail.value.start_time)), 'yyyy-MM-dd');
      const isReplicationAdvance = oldSessionDetail.value.replica?.replica_type === 'REPLICATION_ADVANCE' && newSession.value.replica.replica_type === 'REPLICATION_ADVANCE';
      const isReschedule = start !== startOld;

      if (isReplicationAdvance && isReschedule) {
        return [{ label: 'Cette session', value: 'SINGLE' }];
      }

      if (newSession.value.type && (newSession.value?.treatment_type !== oldSessionDetail.value?.treatment_type)) {
        return [{ label: 'Toutes les séances', value: 'ALL' }];
      }

      if (newSession.value.replica && !isObjectEmpty(newSession.value.replica)) {
        if (sessionReplica) {
          const recurringIntervalChanges = getUnixTime(endOfDay(new Date(sessionReplica.value.replica_end_date))) !== getUnixTime(endOfDay(new Date(fromUnixTime(newSession.value.end_recurring_date))));

          if (recurringIntervalChanges) {
            return [{ label: 'Toutes les séances', value: 'ALL' }];
          }
        }
      }

      if (isReplicationAdvance && !isReschedule && !sessionsWithPeriods.includes(newSession.value.type)) {
        return deleteOptions;
      }

      const sessionTimeChanges = newSession.value.start_time !== oldSessionDetail.value.start_time || newSession.value.end_time !== oldSessionDetail.value.end_time;

      if (sessionsWithPeriods.includes(newSession.value.type)) {
        if (sessionTimeChanges) {
          return [{ label: 'Cette session', value: 'SINGLE' }];
        }

        if (sessionReplica && sessionReplica.value.isReplica && (!oldSessionDetail.value.replica || isObjectEmpty(oldSessionDetail.value.replica))) {
          return [{ label: 'Toutes les séances', value: 'ALL' }];
        }
        return stageRadioOptions;
      }
      return deleteOptions;
    });

    const routeToProducts = () => {
      store.commit('setCustomersToAssign', oldSessionDetail.value.participants);
      store.commit('setCurrentStep', localReferences.productStepper.PRODUCTS);
      router.push({ path: '/products' });
    };

    // provides
    provide('newSession', newSession);
    provide('sessionReplica', sessionReplica);
    provide('resetReplica', resetReplica);
    provide('editable', editable);
    provide('productActivityDialog', productActivityDialog);
    provide('copyDialog', copyDialog);
    provide('typeOfupdate', typeOfupdate);
    provide('oldSessionDetail', oldSessionDetail);
    provide('originalSessionReplica', originalSessionReplica);

    return {
      editOverlayOptions,
      deleteValidation,
      deleteConfirmationModal,
      changeDeleteConfirmationModal,
      canUserPerformAction,
      createEditSession,
      changeEditSessionOverlay,
      editSessionOverlay,
      deleteOptions,
      canShowRadioGroup,
      deleteSession,
      deleteOption,
      updateSessionContentTypes,
      productActivityDialog,
      repriseSessionType,
      canShowGroupeEfforField,
      longSessionTypes,
      canShowFieldsBasedOnSessionType,
      editable,
      handleCreateEditSession,
      copyDialog,
      copySession,
      buttonLabel,
      confirmationModal,
      changeConfirmationModal,
      mandatoryRule,
      newSession,
      loading,
      formValid,
      routeToProducts,
      placesLoading
    };
  }
};
</script>

<style scoped>
  .v-slide-group__prev{
    background-color: red;
  }
  .autocomplete {
    margin-top: 2px;
  }
</style>
